import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plug, Trash, PlusCircle, FloppyDisk, CheckCircle, WarningCircle, Copy, ArrowsClockwise } from "@phosphor-icons/react";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [contact, setContact] = useState({});
  const [contactSaving, setContactSaving] = useState(false);
  const [contactImporting, setContactImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [serverIp, setServerIp] = useState("");
  const [presets, setPresets] = useState([]);
  const [newPreset, setNewPreset] = useState({ name: "", ns: ["", "", "", ""], description: "" });

  const loadSettings = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setForm({
      api_user: data.api_user || "",
      username: data.username || "",
      environment: data.environment || "sandbox",
      api_key: "",
    });
    setContact({
      first_name:   data.default_contact?.first_name   || "",
      last_name:    data.default_contact?.last_name    || "",
      organization: data.default_contact?.organization || "",
      address1:     data.default_contact?.address1     || "",
      address2:     data.default_contact?.address2     || "",
      city:         data.default_contact?.city         || "",
      state:        data.default_contact?.state        || "",
      postal_code:  data.default_contact?.postal_code  || "",
      country:      data.default_contact?.country      || "US",
      phone:        data.default_contact?.phone        || "",
      email:        data.default_contact?.email        || "",
    });
  };

  const loadServerIp = async () => {
    try {
      const { data } = await api.get("/settings/server-ip");
      setServerIp(data.ip || "");
    } catch (_) {}
  };

  const loadPresets = async () => {
    const { data } = await api.get("/dns-presets");
    setPresets(data.items || []);
  };

  useEffect(() => { loadSettings(); loadPresets(); loadServerIp(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      delete body.client_ip;   // no longer user-managed
      if (!body.api_key) delete body.api_key;
      await api.post("/settings", body);
      toast.success("Settings saved");
      await loadSettings();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/test");
      setTestResult({ ok: true, ...data });
      toast.success(`Connected · ${data.environment}`);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  const saveContact = async () => {
    const required = ["first_name", "last_name", "address1", "city", "state",
                      "postal_code", "country", "phone", "email"];
    const missing = required.filter((k) => !contact[k]?.trim());
    if (missing.length) {
      toast.error(`Missing: ${missing.join(", ")}`);
      return;
    }
    setContactSaving(true);
    try {
      await api.post("/settings", { default_contact: contact });
      toast.success("Registrant contact saved");
      await loadSettings();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setContactSaving(false); }
  };

  const importContactFromNamecheap = async () => {
    setContactImporting(true);
    try {
      const { data } = await api.post("/settings/contact/import-from-namecheap");
      setContact((prev) => ({ ...prev, ...data.contact }));
      toast.success("Contact imported from Namecheap — review and click Save");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setContactImporting(false); }
  };

  const addPreset = async () => {
    if (!newPreset.name.trim()) return toast.error("Preset needs a name");
    const ns = newPreset.ns.map((s) => s.trim()).filter(Boolean);
    if (ns.length < 2) return toast.error("At least 2 nameservers");
    try {
      await api.post("/dns-presets", {
        name: newPreset.name.trim(),
        nameservers: ns,
        description: newPreset.description,
      });
      toast.success("Preset added");
      setNewPreset({ name: "", ns: ["", "", "", ""], description: "" });
      await loadPresets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/dns-presets/${id}`);
      toast.success("Preset removed");
      await loadPresets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!settings) return <Layout><div className="overline">Loading…</div></Layout>;

  return (
    <Layout>
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        description="Connect your Namecheap API account and manage reusable DNS presets."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Namecheap API */}
        <Card className="p-6" data-testid="settings-api-card">
          <div className="flex items-center gap-2 mb-4">
            <Plug size={18} />
            <h3 className="font-display text-lg font-bold tracking-tight">Namecheap API</h3>
            {settings.configured ? (
              <StatusBadge variant="success" icon={CheckCircle} className="ml-auto">configured</StatusBadge>
            ) : (
              <StatusBadge variant="warning" icon={WarningCircle} className="ml-auto">not configured</StatusBadge>
            )}
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="overline">API User</Label>
              <Input
                value={form.api_user || ""}
                onChange={(e) => setForm({ ...form, api_user: e.target.value })}
                placeholder="your-api-user"
                className="font-mono text-sm"
                data-testid="api-user-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Username</Label>
              <Input
                value={form.username || ""}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="usually same as API User"
                className="font-mono text-sm"
                data-testid="username-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">API Key</Label>
              <Input
                type="password"
                value={form.api_key || ""}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder={settings.api_key_masked || "paste API key"}
                className="font-mono text-sm"
                data-testid="api-key-input"
              />
              {settings.api_key_masked && (
                <div className="text-[11px] text-muted-foreground font-mono">Current: {settings.api_key_masked}</div>
              )}
            </div>

            {/* Server IP — auto-detected, read-only */}
            <div className="rounded-md bg-muted/50 border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="overline text-muted-foreground text-[10px]">Server IP (auto-detected)</Label>
                <button onClick={loadServerIp} className="text-muted-foreground hover:text-foreground" title="Refresh IP" data-testid="refresh-server-ip-btn">
                  <ArrowsClockwise size={13} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono flex-1 text-foreground" data-testid="server-ip-display">
                  {serverIp || "detecting…"}
                </code>
                {serverIp && (
                  <button onClick={() => { navigator.clipboard.writeText(serverIp); toast.success("IP copied"); }} className="text-muted-foreground hover:text-foreground" data-testid="copy-server-ip-btn">
                    <Copy size={13} />
                  </button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                This IP is injected automatically into every Namecheap API call — you don't need to enter it manually.
                Add it to your whitelist at{" "}
                <a href="https://ap.www.namecheap.com/settings/tools/apiaccess/" target="_blank" rel="noreferrer" className="underline">
                  Namecheap → Profile → Tools → API Access
                </a>.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="overline">Environment</Label>
              <Select value={form.environment || "sandbox"} onValueChange={(v) => setForm({ ...form, environment: v })}>
                <SelectTrigger data-testid="env-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
                <FloppyDisk size={14} className="mr-2" />
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="test-settings-btn">
                {testing ? "Testing…" : "Test connection"}
              </Button>
            </div>
            {testResult && (
              <div className={`mt-3 p-3 rounded-md border text-xs font-mono ${testResult.ok ? "border-success/40 bg-success/10 text-success" : "border-destructive/40 bg-destructive/10 text-destructive"}`}>
                {testResult.ok
                  ? `Connected · ${testResult.environment} · Balance: ${testResult.balances?.available_balance} ${testResult.balances?.currency}`
                  : testResult.error}
              </div>
            )}
          </div>
        </Card>

        {/* Registrant Contact (WHOIS) — used for every new domain purchase */}
        <Card className="p-6 lg:col-span-2" data-testid="settings-contact-card">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-display text-lg font-bold tracking-tight">Registrant Contact (WHOIS)</h3>
            {settings.default_contact?.email ? (
              <StatusBadge variant="success" icon={CheckCircle} className="ml-auto">configured</StatusBadge>
            ) : (
              <StatusBadge variant="warning" icon={WarningCircle} className="ml-auto">required</StatusBadge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-4 max-w-3xl">
            These details are sent as Registrant / Admin / Tech / Billing contacts for every
            domain purchased through this app. Namecheap freezes domains registered with
            placeholder data — use your real, verified info. Click <em>Import from Namecheap</em>{" "}
            to auto-fill from your Namecheap profile address.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="overline">First Name *</Label>
              <Input value={contact.first_name || ""} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} data-testid="contact-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Last Name *</Label>
              <Input value={contact.last_name || ""} onChange={(e) => setContact({ ...contact, last_name: e.target.value })} data-testid="contact-last-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Organization</Label>
              <Input value={contact.organization || ""} onChange={(e) => setContact({ ...contact, organization: e.target.value })} placeholder="(optional)" data-testid="contact-organization" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="overline">Address Line 1 *</Label>
              <Input value={contact.address1 || ""} onChange={(e) => setContact({ ...contact, address1: e.target.value })} data-testid="contact-address1" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Address Line 2</Label>
              <Input value={contact.address2 || ""} onChange={(e) => setContact({ ...contact, address2: e.target.value })} placeholder="(optional)" data-testid="contact-address2" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">City *</Label>
              <Input value={contact.city || ""} onChange={(e) => setContact({ ...contact, city: e.target.value })} data-testid="contact-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">State / Province *</Label>
              <Input value={contact.state || ""} onChange={(e) => setContact({ ...contact, state: e.target.value })} data-testid="contact-state" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Postal Code *</Label>
              <Input value={contact.postal_code || ""} onChange={(e) => setContact({ ...contact, postal_code: e.target.value })} data-testid="contact-postal" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Country * (ISO-2)</Label>
              <Input value={contact.country || ""} onChange={(e) => setContact({ ...contact, country: e.target.value.toUpperCase() })} placeholder="US" maxLength={2} className="font-mono uppercase" data-testid="contact-country" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Phone * (+CC.NUMBER)</Label>
              <Input value={contact.phone || ""} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="+1.4155551212" className="font-mono" data-testid="contact-phone" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="overline">Email *</Label>
              <Input type="email" value={contact.email || ""} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-4 border-t border-border mt-4">
            <Button onClick={saveContact} disabled={contactSaving} data-testid="save-contact-btn">
              <FloppyDisk size={14} className="mr-2" />
              {contactSaving ? "Saving…" : "Save contact"}
            </Button>
            <Button variant="outline" onClick={importContactFromNamecheap} disabled={contactImporting || !settings.configured} title={!settings.configured ? "Configure Namecheap API first" : ""} data-testid="import-contact-btn">
              <ArrowsClockwise size={14} className="mr-2" />
              {contactImporting ? "Importing…" : "Import from Namecheap"}
            </Button>
          </div>
        </Card>

        {/* DNS Presets */}
        <Card className="p-6" data-testid="settings-presets-card">
          <h3 className="font-display text-lg font-bold tracking-tight mb-4">DNS Presets</h3>
          <div className="space-y-2 mb-6">
            {presets.map((p) => (
              <div key={p.id} className="flex items-start gap-3 border border-border rounded-md p-3" data-testid={`preset-row-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{p.name}</span>
                    {p.is_default && <StatusBadge variant="info">default</StatusBadge>}
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground mt-1 break-all">
                    {p.nameservers.join(" · ")}
                  </div>
                  {p.description && <div className="text-[11px] text-muted-foreground mt-1">{p.description}</div>}
                </div>
                {!p.is_default && (
                  <button
                    onClick={() => deletePreset(p.id)}
                    className="h-7 w-7 rounded hover:bg-muted grid place-items-center text-destructive"
                    data-testid={`delete-preset-${p.id}`}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-4">
            <div className="overline mb-3">Add Preset</div>
            <div className="space-y-2">
              <Input
                placeholder="Preset name (e.g. Cloudflare – Main)"
                value={newPreset.name}
                onChange={(e) => setNewPreset({ ...newPreset, name: e.target.value })}
                data-testid="new-preset-name"
              />
              {[0, 1, 2, 3].map((i) => (
                <Input
                  key={i}
                  placeholder={`nameserver ${i + 1}${i < 2 ? " *" : ""}`}
                  value={newPreset.ns[i]}
                  onChange={(e) => setNewPreset({ ...newPreset, ns: newPreset.ns.map((x, j) => j === i ? e.target.value : x) })}
                  className="font-mono text-sm"
                  data-testid={`new-preset-ns-${i}`}
                />
              ))}
              <Input
                placeholder="Description (optional)"
                value={newPreset.description}
                onChange={(e) => setNewPreset({ ...newPreset, description: e.target.value })}
                data-testid="new-preset-desc"
              />
              <Button onClick={addPreset} size="sm" data-testid="add-preset-btn">
                <PlusCircle size={14} className="mr-2" /> Add preset
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
